/* eslint-disable no-use-before-define */
/* eslint-disable no-unused-expressions */
import {
  call,
  put,
  takeEvery,
  takeLatest,
  select,
} from "redux-saga/effects";
import {
  getSchools,
  getSchool,
  setStateSchool,
  getAvailabilities,
  getPendingFeedbacks,
  getFeedbacks,
  addFeedback,
  skipFeedback,
} from "src/web-services";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import { SchoolsCreators, SchoolsTypes } from "../actions";

const authTokenSelector = state => state.user.authToken;
const partnerIdSelector = state => state.user.data.partner_id;
const userIdSelector = state => state.user.data.id;

export function* watchSchoolsRequests() {
  yield takeEvery(
    SchoolsTypes.GET_SCHOOLS_REQUEST,
    requestGetSchools,
  );
  yield takeEvery(SchoolsTypes.GET_SCHOOL_REQUEST, requestGetSchool);
  // takeLatest, not takeEvery: the reducer replaces selected.availabilities
  // wholesale, so an earlier request resolving after a later one would
  // truncate the window the later one had already widened. Cancelling the
  // superseded request is what keeps the map matching the last thing asked
  // for. The slice holds one school's availability and one loading flag, so
  // it only ever has one consumer to serve anyway.
  yield takeLatest(
    SchoolsTypes.GET_AVAILABILITIES_REQUEST,
    requestGetAvailabilities,
  );
  yield takeEvery(
    SchoolsTypes.SET_SLOT_STATE_REQUEST,
    RequestsetSlotState,
  );
  yield takeEvery(
    SchoolsTypes.GET_PENDING_FEEDBACKS_REQUEST,
    requestGetPendingFeedbacks,
  );
  yield takeEvery(
    SchoolsTypes.ADD_FEEDBACK_REQUEST,
    requestAddFeedback,
  );
  yield takeEvery(
    SchoolsTypes.GET_FEEDBACKS_REQUEST,
    requestGetFeedbacks,
  );
  yield takeEvery(
    SchoolsTypes.SKIP_FEEDBACK_REQUEST,
    requestSkipFeedback,
  );
}

export function* requestGetFeedbacks(action) {
  try {
    const { schoolId, page } = action;
    const authToken = yield select(authTokenSelector);
    const response = yield call(
      getFeedbacks,
      authToken,
      schoolId,
      page,
    );
    yield put(
      SchoolsCreators.getFeedbacksSuccess(response.data, page),
    );
  } catch (error) {
    yield put(SchoolsCreators.getFeedbacksFailure());
  }
}

export function* requestGetSchools(action) {
  try {
    const { params, resolve } = action;

    const schoolsSelector = state => state.schools;
    const authToken = yield select(authTokenSelector);
    const partnerId = yield select(partnerIdSelector);
    const { data: currentSchools, params: currentParams } =
      yield select(schoolsSelector);

    const shouldLoadMore =
      currentSchools.length > 0 &&
      isEqual(omit(currentParams, ["page_no"]), params);

    if (!shouldLoadMore) yield put(SchoolsCreators.clearSchools());

    const page = shouldLoadMore ? currentParams.page_no + 1 : 1;
    const requestParams = { ...params };
    requestParams["page_no"] = page;

    const response = yield call(
      getSchools,
      authToken,
      partnerId,
      requestParams,
    );
    resolve && resolve();
    yield put(
      SchoolsCreators.getSchoolsSuccess(response.data, requestParams),
    );
  } catch (error) {
    const { reject } = action;
    reject && reject(error);
    yield put(SchoolsCreators.getSchoolsFailure());
  }
}

export function* requestGetSchool(action) {
  try {
    const { params } = action;
    const authToken = yield select(authTokenSelector);

    const response = yield call(
      getSchool,
      authToken,
      params.partner_id,
    );
    yield put(SchoolsCreators.getSchoolSuccess(response.data));
  } catch (error) {
    yield put(SchoolsCreators.getSchoolFailure());
  }
}

export function* requestGetAvailabilities(action) {
  try {
    const { params } = action;
    const authToken = yield select(authTokenSelector);
    const partnerId = yield select(partnerIdSelector);

    const response = yield call(
      getAvailabilities,
      authToken,
      partnerId,
      params,
    );
    yield put(
      SchoolsCreators.getAvailabilitiesSuccess(
        response.data.availabilities,
      ),
    );
  } catch (error) {
    yield put(SchoolsCreators.getAvailabilitiesFailure());
  }
}

export function* RequestsetSlotState(action) {
  try {
    const { params } = action;
    const authToken = yield select(authTokenSelector);
    const userId = yield select(userIdSelector);
    const response = yield call(
      setStateSchool,
      authToken,
      userId,
      params,
    );
    yield put(SchoolsCreators.setSlotStateSuccess(response));
  } catch (error) {
    yield put(SchoolsCreators.setSlotStateFailure());
  }
}

export function* requestGetPendingFeedbacks() {
  try {
    const authToken = yield select(authTokenSelector);
    const response = yield call(getPendingFeedbacks, authToken);
    yield put(
      SchoolsCreators.getPendingFeedbacksSuccess(response.data),
    );
  } catch (error) {
    yield put(SchoolsCreators.getPendingFeedbacksFailure());
  }
}

export function* requestAddFeedback(action) {
  try {
    const { data, eventId } = action;
    const authToken = yield select(authTokenSelector);
    const response = yield call(
      addFeedback,
      authToken,
      data,
      eventId,
    );
    yield put(SchoolsCreators.addFeedbackSuccess(response));
  } catch (error) {
    yield put(SchoolsCreators.addFeedbackFailure());
  }
}

export function* requestSkipFeedback(action) {
  try {
    const { eventId } = action;
    const authToken = yield select(authTokenSelector);
    yield call(skipFeedback, authToken, eventId);
    yield put(SchoolsCreators.skipFeedbackSuccess());
  } catch (error) {
    yield put(SchoolsCreators.skipFeedbackFailure());
  }
}
